'use client'

import type { ComponentProps, ReactNode } from 'react'

import { Link } from '@/app/components/atoms/Link'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { buildLinkClick } from '@/app/lib/analytics/buildLinkClick'
import { PDP_BUY_CTA_CLICK_MODULE_NAME } from '@/app/lib/analytics/constants'

interface HeroTrackedLinkProps {
  readonly href: string
  /** Texto visible del enlace; se slugifica para `link_text`. */
  readonly label: string
  readonly children?: ReactNode
  /** Enlace con estilo `Link` (atom) o `<a>` plano (artistas). */
  readonly plain?: boolean
  readonly className?: string
  readonly dark?: boolean
  readonly size?: ComponentProps<typeof Link>['size']
}

/**
 * Enlace de la cabecera de la PDP (recinto, artista, landing) con su `click`
 * (`content_link`, módulo `product_header`). Fuera del provider navega sin tracking.
 */
export function HeroTrackedLink({
  href,
  label,
  children,
  plain = false,
  className,
  dark,
  size,
}: HeroTrackedLinkProps) {
  const analytics = useOptionalPageAnalytics()

  function handleClick() {
    analytics?.trackClick(
      buildLinkClick({
        clickType: 'content_link',
        label,
        href,
        moduleName: PDP_BUY_CTA_CLICK_MODULE_NAME,
      }),
    )
  }

  if (plain) {
    return (
      <a className={className} href={href} onClick={handleClick}>
        {children ?? label}
      </a>
    )
  }

  return (
    <Link href={href} dark={dark} size={size} className={className} onClick={handleClick}>
      {children ?? label}
    </Link>
  )
}
